import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { MdDelete, MdOutlineCloudUpload } from "react-icons/md";
import { handleApiCall, handleAdminApiCall } from "../../apis";
import { endpoint } from "../../utils/endpoints";

const PH = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='300'%3E%3Crect fill='%23eee' width='400' height='300'/%3E%3C/svg%3E";

const ViewService = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const listingIdFromState = location?.state?.id;

    const [listingData, setListingData] = useState({});
    const [loading, setLoading] = useState(true);
    const [modalImage, setModalImage] = useState(null);
    const [modalVideo, setModalVideo] = useState(null);
    const [eventTypeList, setEventTypeList] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleteSubmitting, setDeleteSubmitting] = useState(false);
    const [mediaTab, setMediaTab] = useState("photos");

    useEffect(() => {
        const fetchEventTypes = async () => {
            try {
                const res = await handleAdminApiCall("get", endpoint.getEventTypes);
                if (res?.data?.code === 200) {
                    const rawList = res?.data?.data || [];
                    const list = Array.isArray(rawList)
                        ? rawList
                            .map((item) => {
                                if (!item) return null;
                                const id = item._id || item.id || item.value || null;
                                const name = item.name || item.label || item.eventType || item.title || "";
                                return id && name ? { _id: id, name } : null;
                            })
                            .filter(Boolean)
                        : [];
                    setEventTypeList(list);
                }
            } catch (e) {
                setEventTypeList([]);
            }
        };
        fetchEventTypes();
    }, []);

    // Fetch listing by id
    useEffect(() => {
        if (!listingIdFromState) {
            setLoading(false);
            return;
        }
        const id = typeof listingIdFromState === "string" ? listingIdFromState : listingIdFromState?._id;
        if (!id) {
            setLoading(false);
            return;
        }
        const fetchListing = async () => {
            try {
                setLoading(true);
                const res = await handleApiCall("get", endpoint.viewService(id), {});
                console.log("View service API response:", res);
                const raw = res?.data;
                const data = raw?.data ?? raw ?? {};
                setListingData(typeof data === "object" && data !== null ? data : {});
            } catch (e) {
                console.log("Error fetching view service:", e);
                setListingData({});
            } finally {
                setLoading(false);
            }
        };
        fetchListing();
    }, [listingIdFromState]);

    // Derive tag names from API response (tag_ids are populated with { _id, tag_name })
    const getTagName = (tagItem) => {
        if (!tagItem) return "";
        if (typeof tagItem === "object") return tagItem.tag_name || tagItem.name || tagItem._id || "";
        return String(tagItem);
    };

    // Derive label names from API response (labels are populated with { _id, label_name })
    const getLabelName = (labelItem) => {
        if (!labelItem) return "";
        if (typeof labelItem === "object") return labelItem.label_name || labelItem.name || labelItem._id || "";
        return String(labelItem);
    };

    // Resolve event type IDs to names using event types API list
    const getEventTypeDisplay = (eventTypeValue) => {
        if (eventTypeValue == null || eventTypeValue === "") return "-";
        if (Array.isArray(eventTypeValue)) {
            const names = eventTypeValue
                .map((item) => {
                    if (item == null) return "";
                    if (typeof item === "object") return item?.name ?? item?._id ?? "";
                    const idOrName = String(item).trim();
                    const found = eventTypeList.find((et) => et._id === idOrName || String(et._id) === idOrName);
                    return found ? found.name : idOrName;
                })
                .filter(Boolean);
            return names.length > 0 ? names.join(", ") : "-";
        }
        if (typeof eventTypeValue === "string") {
            const found = eventTypeList.find((et) => et._id === eventTypeValue || String(et._id) === eventTypeValue);
            return found ? found.name : eventTypeValue;
        }
        if (typeof eventTypeValue === "object" && eventTypeValue?.name) return eventTypeValue.name;
        return "-";
    };

    const listingIdForActions =
        listingData?._id ||
        (typeof listingIdFromState === "string"
            ? listingIdFromState
            : listingIdFromState?._id);

    const navigateToEdit = () => {
        navigate("/service-management/add-service", { state: { id: listingData } });
    };

    const handleDeleteConfirm = async () => {
        if (!listingIdForActions) {
            toast.error("Service ID not found");
            return;
        }
        try {
            setDeleteSubmitting(true);
            const res = await handleApiCall(
                "delete",
                endpoint.deleteServiceListing(listingIdForActions)
            );
            if (res?.data?.code === 200) {
                toast.success("Service listing deleted successfully");
                setShowDeleteModal(false);
                navigate("/service-management");
            } else {
                toast.error(
                    res?.response?.data?.errorMessage ||
                        res?.response?.data?.message ||
                        res?.data?.message ||
                        "Failed to delete service listing"
                );
            }
        } catch (err) {
            console.log("Error deleting service listing:", err);
            toast.error("Failed to delete service listing");
        } finally {
            setDeleteSubmitting(false);
        }
    };

    const eventTypeDisplay = getEventTypeDisplay(listingData.eventType ?? listingData.attributes?.eventType);

    // Specification rows by service category (aligned with add/edit form per service type)
    const getSpecificationRows = () => {
        const d = listingData;
        const cat = (d.service_id?.name || "").toLowerCase();
        const isVenue = cat === "venue" || cat.includes("venue");
        const isCatering = cat === "catering" || cat.includes("catering");
        const isDecor = cat === "decor" || cat.includes("decor") || cat === "design and decor";
        const isEntertainment = cat === "entertainment" || cat.includes("entertainment");
        const isClothing = cat === "clothing" || cat.includes("clothing");
        const isBeautyWellness = cat === "beauty and wellness" || cat.includes("beauty") || cat.includes("wellness");
        const isPandits = cat === "pandits" || cat.includes("pandit");
        const isInvitationsGifting = cat === "invitations" || cat === "gifting" || cat.includes("invitation") || cat.includes("gift");
        const isRecycling = cat === "recycling" || cat.includes("recycling");
        const isManagement = cat === "management services" || cat.includes("management") || cat.includes("service");

        const fmt = (v) => (v != null && v !== "" ? v : null);
        const rows = [
            ["Established Year", fmt(d.experience ?? d.attributes?.experience)],
            ["Customization", d.customization ? "Available" : "Not Available"],
            ["Event Type", eventTypeDisplay],
            ["Travel & Accommodation Charges", d.travelAndAccommodationCharges ? `Rs. ${d.travelAndAccommodationCharges}/-` : null],
            ["Package", fmt(d.package ?? d.attributes?.package)],
            ["Discount", d.discount != null ? `${d.discount}%` : null],
            ["Travel Policy", fmt(d.travelPolicy ?? d.attributes?.travelPolicy)],
        ];
        if (isVenue || isCatering) {
            if (isVenue) {
                rows.push(["Guest Count", fmt(d.guestCount ?? d.attributes?.guestCount)]);
            }
            rows.push(
                ["Capacity", fmt(d.capacity ?? d.attributes?.capacity)],
                ["Rent Cost", d.rentCost ? `Rs. ${d.rentCost}/-` : null],
                ["Star Category", fmt(d.starCategory ?? d.attributes?.starCategory)],
                ["Room Count", fmt(d.roomCount ?? d.attributes?.roomCount)],
                ["Dimensions", fmt(d.dimensions ?? d.attributes?.dimensions)],
                ["Accessibility", d.accessibility ? "Wheelchair Available" : "Not Available"],
                ["AV Equipment", d.avEquipment ? "Yes" : "No"],
                ["Wifi", d.wifi ? "Yes" : "No"],
                ["Space Type", fmt(d.spaceType ?? d.attributes?.spaceType)],
                ["Parking Capacity", fmt(d.parkingCapacity ?? d.attributes?.parkingCapacity)],
                ["Catering Policy", fmt(d.cateringPolicy ?? d.attributes?.cateringPolicy)],
                ["DJ Policy", fmt(d.djPolicy ?? d.attributes?.djPolicy)],
                ["Alcohol Policy", fmt(d.alcoholPolicy ?? d.attributes?.alcoholPolicy)],
                ["Decor Policy", fmt(d.decorPolicy ?? d.attributes?.decorPolicy)],
                ["Location", fmt(d.location)]
            );
        } else if (isDecor) {
            rows.push(["Theme", fmt(d.theme ?? d.attributes?.theme)], ["Location", fmt(d.location)]);
        } else if (isEntertainment) {
            rows.push(
                ["Rent Cost", d.rentCost ? `Rs. ${d.rentCost}/-` : null],
                ["Star Category", fmt(d.starCategory ?? d.attributes?.starCategory)],
                ["Location", fmt(d.location)]
            );
        } else if (isClothing) {
            rows.push(
                ["Signature Style", fmt(d.signatureStyle ?? d.attributes?.signatureStyle)],
                ["Gender", fmt(d.gender ?? d.attributes?.gender)],
                ["Location", fmt(d.location)]
            );
        } else if (isBeautyWellness) {
            rows.push(
                ["Type of Concern", fmt(d.typeOfConcern ?? d.attributes?.typeOfConcern)],
                ["Language Preference", fmt(d.languagePreference ?? d.attributes?.languagePreference)],
                ["Location", fmt(d.location)]
            );
        } else if (isPandits) {
            rows.push(
                ["Language Preference", fmt(d.languagePreference ?? d.attributes?.languagePreference)],
                ["Location", fmt(d.location)]
            );
        } else if (isInvitationsGifting) {
            rows.push(
                ["Recipient Type", fmt(d.recipientType ?? d.attributes?.recipientType)],
                ["Size/Dimensions", fmt(d.sizeDimensions ?? d.size ?? d.attributes?.sizeDimensions)],
                ["Pick-Up Options", fmt(d.pickUpOptions ?? d.attributes?.pickUpOptions)],
                ["Customer Reviews", fmt(d.customerReviews ?? d.attributes?.customerReviews)],
                ["Bulk Purchase Options", fmt(d.bulkPurchaseOptions ?? d.attributes?.bulkPurchaseOptions)],
                ["Certifications", fmt(d.certifications ?? d.attributes?.certifications)],
                ["Delivery Options", fmt(d.deliveryOptions ?? d.attributes?.deliveryOptions)],
                ["Return Policy", fmt(d.returnPolicy ?? d.attributes?.returnPolicy)],
                ["Availability", fmt(d.availability ?? d.attributes?.availability)],
                ["Theme/Style", fmt(d.themeStyle ?? d.attributes?.themeStyle)],
                ["Location", fmt(d.location)]
            );
        } else if (isRecycling) {
            rows.push(
                ["Pick-Up Options", fmt(d.pickUpOptions ?? d.attributes?.pickUpOptions)],
                ["Customer Reviews", fmt(d.customerReviews ?? d.attributes?.customerReviews)],
                ["Certifications", fmt(d.certifications ?? d.attributes?.certifications)],
                ["Location", fmt(d.location)]
            );
        } else if (isManagement) {
            rows.push(
                ["Language Preference", fmt(d.languagePreference ?? d.attributes?.languagePreference)],
                ["Location", fmt(d.location)]
            );
        } else {
            rows.push(["Location", fmt(d.location)]);
        }
        return rows;
    };
    const specificationRows = getSpecificationRows();

    const tagIds = listingData.tag_ids || [];
    const tagNames = tagIds.map(getTagName).filter(Boolean);
    const labelIds = listingData.labels || [];
    const labelNames = labelIds.map(getLabelName).filter(Boolean);

    const slotsArray = Array.isArray(listingData.serviceAvailability?.slots)
        ? listingData.serviceAvailability.slots
        : [];

    /** Normalize strings, comma-separated strings, or arrays (matches add-service payload shapes). */
    const normalizeMediaList = (raw) => {
        if (raw == null) return [];
        if (Array.isArray(raw)) return raw.filter((x) => x != null && x !== "");
        if (typeof raw === "string") return raw.split(",").map((s) => s.trim()).filter(Boolean);
        return [];
    };

    const resolveMediaUrl = (item) => {
        if (item == null || item === "") return "";
        if (typeof item === "string") return item;
        return (
            item.url ||
            item.fileName ||
            item.path ||
            item.src ||
            ""
        );
    };

    const galleryImages = normalizeMediaList(
        listingData.images ?? listingData.gallery_images ?? listingData.galleryImages
    );
    const galleryVideos = normalizeMediaList(
        listingData.videos ?? listingData.video_gallery ?? listingData.videoGallery
    );
    const portfolioItems = normalizeMediaList(listingData.portfolio);

    const serviceCatLower = (listingData.service_id?.name || "").toLowerCase();
    const isVenueOnly =
        serviceCatLower === "venue" || serviceCatLower.includes("venue");
    const venueMenuItems = normalizeMediaList(
        listingData.menuOptions ?? listingData.menu_options
    );

    if (loading) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <div className="w-12 h-12 border-4 border-[#A4B792] border-t-[#E67E22] rounded-full animate-spin" aria-hidden="true" />
                <p className="text-gray-600 font-Montserratmedium">Loading service details...</p>
            </div>
        );
    }

    if (!listingIdFromState || (Object.keys(listingData).length === 0 && !loading)) {
        return (
            <div className="min-h-screen flex flex-col items-center justify-center gap-4">
                <p className="text-gray-500 font-Montserratregular">No listing found.</p>
                <button
                    onClick={() => navigate(-1)}
                    className="text-[#E67E22] border border-[#E67E22] bg-white px-4 py-1.5 text-sm font-BonaNovabold hover:bg-[#E67E22] hover:text-white transition"
                >
                    Back
                </button>
            </div>
        );
    }

    return (
        <>
            <div className="min-h-screen pb-8">
                <div className="border-t border-[#A4B792] mb-6"></div>
                
                <div className="flex justify-between items-center mb-6">
                    <button
                        onClick={() => navigate(-1)}
                        className="text-[#E67E22] border border-[#E67E22] bg-white px-4 py-1.5 text-sm font-BonaNovabold hover:bg-[#E67E22] hover:text-white transition"
                    >
                        Back
                    </button>
                    <div className="flex gap-3">
                        <button
                            type="button"
                            onClick={() => setShowDeleteModal(true)}
                            className="text-[#E67E22] border border-[#E67E22] bg-white px-4 py-1.5 text-sm font-BonaNovabold hover:bg-[#E67E22] hover:text-white transition"
                        >
                            Remove
                        </button>
                        <button
                            onClick={() => {
                                navigate("/service-management/add-service", { state: { id: listingData } });
                            }}
                            className="bg-[#E67E22] text-white px-8 py-1.5 text-sm font-BonaNovabold hover:bg-[#d66a1a] transition"
                        >
                            Edit
                        </button>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
                    <div className="flex flex-col">
                        <div className="rounded-lg overflow-hidden cursor-pointer" onClick={() => { const u = typeof listingData.cover_image === "object" ? (listingData.cover_image?.url || listingData.cover_image?.fileName) : listingData.cover_image; if (u) setModalImage(u); }}>
                            <img
                                src={(typeof listingData.cover_image === "object" && (listingData.cover_image?.url || listingData.cover_image?.fileName)) ? (listingData.cover_image.url || listingData.cover_image.fileName) : (listingData.cover_image || PH)}
                                alt="Service"
                                className="w-full h-full min-h-[350px] object-cover rounded-lg"
                                onError={(e) => { e.target.src = PH; }}
                            />
                        </div>
                    </div>

                    <div className="flex flex-col">
                        <div className="bg-white border border-[#A4B792] rounded-lg p-4 flex flex-col flex-1">
                            <div className="space-y-4 flex-1">
                                <div className="flex justify-between items-start gap-2">
                                    <label className="text-sm text-black-800 whitespace-nowrap font-Montserratmedium">Name/Title:</label>
                                    <p className="text-gray-500 text-base font-Montserratregular text-right break-words">{listingData.title || "-"}</p>
                                </div>
                                <div className="flex justify-between items-start gap-2">
                                    <label className="text-sm text-gray-600 whitespace-nowrap font-Montserratmedium">Service Sub Category:</label>
                                    <p className="text-sm text-gray-500 font-Montserratregular text-right">{listingData.sub_categoty_id?.name || listingData.sub_category_id?.name || "-"}</p>
                                </div>
                                <div className="flex-1">
                                    <h3 className="text-base font-Montserratmedium text-[#262626] mb-2">Description</h3>
                                    <div className="bg-[#A4B79220] p-3 rounded">
                                        <p className="text-[#262626] text-sm leading-relaxed font-Montserratregular">{listingData.description || "-"}</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div>
                        <div className="bg-white border border-[#A4B792] rounded-lg p-4 h-full flex flex-col">
                            <div className="flex-1">
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <label className="text-sm text-gray-600 font-Montserratmedium">Base Price</label>
                                    <p className="text-[#262626] font-Montserratmedium">{listingData.basePrice ? `Rs. ${listingData.basePrice}/-` : "-"}</p>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <label className="text-sm text-gray-600 font-Montserratmedium">Actual Price</label>
                                    <p className="text-[#262626] font-Montserratmedium">{listingData.price ? `Rs. ${listingData.price}/-` : "-"}</p>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <label className="text-sm text-gray-600 font-Montserratmedium">Tax</label>
                                    <p className="text-[#262626] font-Montserratmedium">{listingData.tax ? `${listingData.tax}%` : "-"}</p>
                                </div>
                                <div className="flex justify-between items-center py-2 border-b border-gray-200">
                                    <label className="text-sm text-gray-600 font-Montserratmedium">Category</label>
                                    <p className="text-[#262626] font-Montserratmedium text-right">{listingData.service_id?.name || listingData.sub_categoty_id?.name || "-"}</p>
                                </div>
                                <div className="py-2 border-b border-gray-200">
                                    <label className="text-sm text-gray-600 block mb-1 font-Montserratmedium">Location - City</label>
                                    <p className="text-[#262626] font-Montserratmedium">{listingData.location || "-"}</p>
                                </div>
                                <div className="py-2">
                                    <label className="text-sm text-gray-600 block mb-1 font-Montserratmedium">Full Address</label>
                                    <p className="text-[#262626] font-Montserratmedium">{listingData.address || "-"}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="mt-8">
                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                        <div>
                            <h3 className="text-[#F4802C] text-sm font-Montserratmedium mb-2">Specification</h3>
                            <div className="bg-[#F9FAF7] border border-[#E5E7EB] rounded-lg overflow-hidden">
                                <div className="divide-y divide-gray-200">
                                    {specificationRows.map(([label, value]) => (
                                        <div key={label} className="flex justify-between items-center px-6 py-3 gap-4">
                                            <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">{label}</span>
                                            <span className="text-sm text-[#262626] font-Montserratregular text-right break-words">{value ?? "-"}</span>
                                    </div>
                                    ))}
                                </div>
                            </div>
                        </div>

                        <div>
                            <h3 className="text-[#F4802C] text-sm font-Montserratmedium mb-2">Attributes</h3>
                            <div className="bg-[#F9FAF7] border border-[#E5E7EB] rounded-lg overflow-hidden">
                                <div className="divide-y divide-gray-200">
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">What's Included</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes?.whatsIncluded || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">What's Excluded</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes?.whatsExcluded || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">What Can We Expect</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes?.whatToExpect || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Cancellation Policy</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes?.cancellationPolicy || "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Tags</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{tagNames.length > 0 ? tagNames.join(", ") : "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Label</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{labelNames.length > 0 ? labelNames.join(", ") : "-"}</span>
                                    </div>
                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Inquiry Or Booking</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes?.inquiryType || "-"}</span>
                                    </div>
                                    {(listingData.attributes?.lens != null && listingData.attributes.lens !== "") && (
                                        <div className="flex justify-between items-center px-6 py-3 gap-4">
                                            <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Lens</span>
                                            <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.attributes.lens}</span>
                                        </div>
                                    )}
                                    {(listingData.cancelPolicy != null && listingData.cancelPolicy !== "") && (
                                        <div className="flex justify-between items-center px-6 py-3 gap-4">
                                            <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Cancel Policy</span>
                                            <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.cancelPolicy}</span>
                                        </div>
                                    )}
                                </div>
                            </div>

                            {(listingData.status != null || listingData.is_active != null || listingData.reason) && (
                                <div className="bg-[#F9FAF7] border border-[#E5E7EB] rounded-lg overflow-hidden mt-4">
                                    <div className="divide-y divide-gray-200">
                                        {listingData.status != null && (
                                            <div className="flex justify-between items-center px-6 py-3 gap-4">
                                                <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Status</span>
                                                <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.status ? "Active" : "Inactive"}</span>
                                            </div>
                                        )}
                                        {listingData.is_active != null && (
                                            <div className="flex justify-between items-center px-6 py-3 gap-4">
                                                <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Is Active</span>
                                                <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.is_active ? "Yes" : "No"}</span>
                                            </div>
                                        )}
                                        {listingData.reason != null && listingData.reason !== "" && (
                                            <div className="flex justify-between items-center px-6 py-3 gap-4">
                                                <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Reason</span>
                                                <span className="text-sm text-[#262626] font-Montserratregular text-right">{listingData.reason}</span>
                                            </div>
                                        )}
                                        {(listingData.createdAt || listingData.updatedAt) && (
                                            <>
                                                {listingData.createdAt && (
                                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Created</span>
                                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{new Date(listingData.createdAt).toLocaleString()}</span>
                                                    </div>
                                                )}
                                                {listingData.updatedAt && (
                                                    <div className="flex justify-between items-center px-6 py-3 gap-4">
                                                        <span className="text-sm text-gray-600 font-Montserratmedium shrink-0">Updated</span>
                                                        <span className="text-sm text-[#262626] font-Montserratregular text-right">{new Date(listingData.updatedAt).toLocaleString()}</span>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </div>
                                </div>
                            )}

                            {isVenueOnly ? (
                                <>
                                    <h3 className="text-[#F4802C] text-sm font-Montserratmedium mt-6 mb-2 uppercase tracking-wide">
                                        Menu
                                    </h3>
                                    <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAF7] px-4 py-4">
                                        <p className="mb-3 text-xs font-Montserratmedium text-[#262626]">
                                            Upload photos
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            <button
                                                type="button"
                                                onClick={navigateToEdit}
                                                className="flex aspect-[4/3] min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-2 transition hover:border-[#F4802C]/70"
                                            >
                                                <MdOutlineCloudUpload
                                                    className="text-4xl text-[#F4802C]"
                                                    aria-hidden
                                                />
                                                <span className="text-center text-xs font-Montserratmedium leading-tight text-[#F4802C]">
                                                    Upload new photo
                                                </span>
                                            </button>
                                            {venueMenuItems.map((item, index) => {
                                                const u = resolveMediaUrl(item);
                                                return (
                                                    <div
                                                        key={`menu-${item?._id ?? index}`}
                                                        className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-200"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="absolute inset-0 z-0 flex h-full w-full"
                                                            onClick={() => u && setModalImage(u)}
                                                            aria-label={`View menu ${index + 1}`}
                                                        >
                                                            <img
                                                                src={u || PH}
                                                                alt={`Menu ${index + 1}`}
                                                                className="pointer-events-none h-full w-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.src = PH;
                                                                }}
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigateToEdit();
                                                            }}
                                                            className="absolute bottom-2 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/10 transition hover:bg-gray-50"
                                                            aria-label="Edit listing to change menu images"
                                                        >
                                                            <MdDelete
                                                                className="text-lg text-red-500"
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                </>
                            ) : null}

                            <h3 className="text-[#F4802C] text-sm font-Montserratmedium mt-6 mb-2">Availability</h3>
                            <div className="bg-[#F9FAF7] border border-[#E5E7EB] rounded-lg overflow-hidden">
                                {listingData.serviceAvailability?.timezone && (
                                    <div className="px-6 py-3 border-b border-gray-200">
                                        <span className="text-sm text-gray-600 font-Montserratmedium">Timezone</span>
                                        <span className="text-sm text-[#262626] font-Montserratregular ml-2">{listingData.serviceAvailability.timezone}</span>
                                    </div>
                                )}
                                <div className="divide-y divide-gray-200">
                                    {["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"].map((day) => {
                                        const dayData = listingData.serviceAvailability?.[day];
                                        const isClosed = dayData?.isClosed !== undefined ? dayData.isClosed : true;
                                        return (
                                            <div key={day} className="flex justify-between items-center px-6 py-3">
                                                <span className="text-sm text-gray-600 capitalize font-Montserratmedium">{day}</span>
                                                <span className="text-sm text-[#262626] font-Montserratregular">{isClosed ? "Closed" : "Open"}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <h3 className="text-[#F4802C] text-sm font-Montserratmedium mt-6 mb-2">Slots</h3>
                            <div className="bg-[#F9FAF7] border border-[#E5E7EB] rounded-lg overflow-hidden">
                                <div className="divide-y divide-gray-200">
                                    {["Morning", "Afternoon", "Evening", "Night"].map((slot) => {
                                        const isAvailable = slotsArray.includes(slot);
                                        return (
                                            <div key={slot} className="flex justify-between items-center px-6 py-3">
                                                <span className="text-sm text-gray-600 font-Montserratmedium">{slot}</span>
                                                <span className="text-sm text-[#262626] font-Montserratregular">{isAvailable ? "Available" : "Not Available"}</span>
                                            </div>
                                        );
                                    })}
                                </div>
                            </div>

                            <h3 className="text-[#F4802C] text-sm font-Montserratmedium mt-6 mb-2 uppercase tracking-wide">
                                Media
                            </h3>
                            <div className="rounded-lg border border-[#E5E7EB] bg-[#F9FAF7] px-4 py-4">
                                <div className="mb-4 flex flex-wrap gap-2">
                                    {[
                                        { id: "photos", label: "Photo gallery" },
                                        { id: "videos", label: "Video gallery" },
                                        { id: "portfolio", label: "Portfolio" },
                                    ].map((tab) => (
                                        <button
                                            key={tab.id}
                                            type="button"
                                            onClick={() => setMediaTab(tab.id)}
                                            className={`rounded-md border px-4 py-2 text-sm font-Montserratmedium transition ${
                                                mediaTab === tab.id
                                                    ? "border-[#F4802C] bg-[#F4802C] text-white"
                                                    : "border-[#E5E7EB] bg-white text-[#F4802C] hover:border-[#F4802C]/50"
                                            }`}
                                        >
                                            {tab.label}
                                        </button>
                                    ))}
                                </div>

                                {mediaTab === "photos" && (
                                    <div>
                                        <p className="mb-3 text-xs font-Montserratmedium text-[#262626]">
                                            Upload photos
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            <button
                                                type="button"
                                                onClick={navigateToEdit}
                                                className="flex aspect-[4/3] min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-2 transition hover:border-[#F4802C]/70"
                                            >
                                                <MdOutlineCloudUpload
                                                    className="text-4xl text-[#F4802C]"
                                                    aria-hidden
                                                />
                                                <span className="text-center text-xs font-Montserratmedium leading-tight text-[#F4802C]">
                                                    Upload new photo
                                                </span>
                                            </button>
                                            {galleryImages.map((src, index) => {
                                                const url = resolveMediaUrl(src);
                                                return (
                                                    <div
                                                        key={`img-${index}`}
                                                        className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-200"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="absolute inset-0 z-0 flex h-full w-full"
                                                            onClick={() => url && setModalImage(url)}
                                                            aria-label={`View gallery ${index + 1}`}
                                                        >
                                                            <img
                                                                src={url || PH}
                                                                alt={`Gallery ${index + 1}`}
                                                                className="pointer-events-none h-full w-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.src = PH;
                                                                }}
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigateToEdit();
                                                            }}
                                                            className="absolute bottom-2 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/10 transition hover:bg-gray-50"
                                                            aria-label="Edit listing to change gallery photos"
                                                        >
                                                            <MdDelete
                                                                className="text-lg text-red-500"
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {galleryImages.length === 0 ? (
                                            <p className="mt-3 text-center text-xs font-Montserratmedium text-[#6B6B6B]">
                                                No photos yet — use upload to add from edit
                                            </p>
                                        ) : null}
                                    </div>
                                )}

                                {mediaTab === "videos" && (
                                    <div>
                                        <p className="mb-3 text-xs font-Montserratmedium text-[#262626]">
                                            Upload videos
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            <button
                                                type="button"
                                                onClick={navigateToEdit}
                                                className="flex aspect-[4/3] min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-2 transition hover:border-[#F4802C]/70"
                                            >
                                                <MdOutlineCloudUpload
                                                    className="text-4xl text-[#F4802C]"
                                                    aria-hidden
                                                />
                                                <span className="text-center text-xs font-Montserratmedium leading-tight text-[#F4802C]">
                                                    Upload new video
                                                </span>
                                            </button>
                                            {galleryVideos.map((videoSrc, index) => {
                                                const vUrl = resolveMediaUrl(videoSrc);
                                                return (
                                                    <div
                                                        key={`vid-${index}`}
                                                        className="relative aspect-[4/3] overflow-hidden rounded-lg bg-gray-300"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="absolute inset-0 z-0 flex h-full w-full items-center justify-center"
                                                            onClick={() => vUrl && setModalVideo(vUrl)}
                                                            aria-label={`Play video ${index + 1}`}
                                                        >
                                                            <video
                                                                src={vUrl || ""}
                                                                className="pointer-events-none h-full w-full object-cover"
                                                                muted
                                                                playsInline
                                                                onError={(e) => {
                                                                    e.target.style.display = "none";
                                                                }}
                                                            />
                                                            <span className="pointer-events-none absolute inset-0 flex items-center justify-center bg-black/30">
                                                                <span className="flex h-12 w-12 items-center justify-center rounded-full bg-white/90">
                                                                    <span className="ml-1 border-l-[12px] border-l-[#E67E22] border-y-[8px] border-y-transparent" />
                                                                </span>
                                                            </span>
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigateToEdit();
                                                            }}
                                                            className="absolute bottom-2 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/10 transition hover:bg-gray-50"
                                                            aria-label="Edit listing to change videos"
                                                        >
                                                            <MdDelete
                                                                className="text-lg text-red-500"
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {galleryVideos.length === 0 ? (
                                            <p className="mt-3 text-center text-xs font-Montserratmedium text-[#6B6B6B]">
                                                No videos yet — use upload to add from edit
                                            </p>
                                        ) : null}
                                    </div>
                                )}

                                {mediaTab === "portfolio" && (
                                    <div>
                                        <p className="mb-3 text-xs font-Montserratmedium text-[#262626]">
                                            Upload photos
                                        </p>
                                        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4">
                                            <button
                                                type="button"
                                                onClick={navigateToEdit}
                                                className="flex aspect-[4/3] min-h-[120px] flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#D1D5DB] bg-white px-2 transition hover:border-[#F4802C]/70"
                                            >
                                                <MdOutlineCloudUpload
                                                    className="text-4xl text-[#F4802C]"
                                                    aria-hidden
                                                />
                                                <span className="text-center text-xs font-Montserratmedium leading-tight text-[#F4802C]">
                                                    Upload new photo
                                                </span>
                                            </button>
                                            {portfolioItems.map((item, index) => {
                                                const u = resolveMediaUrl(item);
                                                return (
                                                    <div
                                                        key={`doc-${index}`}
                                                        className="relative aspect-[4/3] overflow-hidden rounded-lg border border-[#E5E7EB] bg-white"
                                                    >
                                                        <button
                                                            type="button"
                                                            className="absolute inset-0 z-0 flex h-full w-full"
                                                            onClick={() => u && setModalImage(u)}
                                                            aria-label={`View portfolio ${index + 1}`}
                                                        >
                                                            <img
                                                                src={u || PH}
                                                                alt={`Portfolio ${index + 1}`}
                                                                className="pointer-events-none h-full w-full object-cover"
                                                                onError={(e) => {
                                                                    e.target.src = PH;
                                                                }}
                                                            />
                                                        </button>
                                                        <button
                                                            type="button"
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                navigateToEdit();
                                                            }}
                                                            className="absolute bottom-2 left-1/2 z-10 flex h-8 w-8 -translate-x-1/2 items-center justify-center rounded-full bg-white shadow-md ring-1 ring-black/10 transition hover:bg-gray-50"
                                                            aria-label="Edit listing to change portfolio"
                                                        >
                                                            <MdDelete
                                                                className="text-lg text-red-500"
                                                                aria-hidden
                                                            />
                                                        </button>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                        {portfolioItems.length === 0 ? (
                                            <p className="mt-3 text-center text-xs font-Montserratmedium text-[#6B6B6B]">
                                                No portfolio images yet — use upload to add from edit
                                            </p>
                                        ) : null}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Image modal - only show when we have a valid string URL */}
            {modalImage && typeof modalImage === "string" && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setModalImage(null)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Escape" && setModalImage(null)}
                >
                    <button
                        type="button"
                        onClick={(e) => { e.stopPropagation(); setModalImage(null); }}
                        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 text-black flex items-center justify-center text-xl leading-none"
                    >
                        &times;
                    </button>
                    <img
                        src={modalImage}
                        alt="Full size"
                        className="max-h-[90vh] max-w-full object-contain rounded"
                        onClick={(e) => e.stopPropagation()}
                        onError={(e) => { e.target.src = PH; }}
                    />
                </div>
            )}

            {/* Video modal */}
            {modalVideo && (
                <div
                    className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/70 p-4"
                    onClick={() => setModalVideo(null)}
                    role="button"
                    tabIndex={0}
                    onKeyDown={(e) => e.key === "Escape" && setModalVideo(null)}
                >
                    <button
                        type="button"
                        onClick={() => setModalVideo(null)}
                        className="absolute top-4 right-4 z-10 w-10 h-10 rounded-full bg-white/90 text-black flex items-center justify-center text-xl leading-none"
                    >
                        &times;
                    </button>
                    <div className="w-full max-w-4xl aspect-video bg-black rounded overflow-hidden" onClick={(e) => e.stopPropagation()}>
                        <video
                            src={modalVideo}
                            controls
                            autoPlay
                            className="w-full h-full"
                        />
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center">
                    <div
                        className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm"
                        onClick={() => !deleteSubmitting && setShowDeleteModal(false)}
                        role="presentation"
                    />
                    <div className="relative bg-white rounded-lg shadow-xl p-6 max-w-md w-full mx-4 z-[10000]">
                        <p className="text-center mb-6 font-bold text-lg text-[#262626]">
                            Are you sure you want delete this Service?
                        </p>
                        <div className="flex gap-4 justify-center">
                            <button
                                type="button"
                                onClick={() => setShowDeleteModal(false)}
                                disabled={deleteSubmitting}
                                className="bg-white border border-gray-300 text-[#262626] px-8 py-2 font-medium hover:bg-gray-50 transition disabled:opacity-50"
                            >
                                No
                            </button>
                            <button
                                type="button"
                                onClick={handleDeleteConfirm}
                                disabled={deleteSubmitting}
                                className="bg-[#A4B792] text-white px-8 py-2 font-medium hover:bg-[#8a9c68] transition disabled:opacity-50"
                            >
                                {deleteSubmitting ? "Deleting..." : "Yes"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

export default ViewService;
